import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { navBridge } from '../services/nav-bridge';

const goToTasks = async () => {
  await navBridge.go('/tasks');
};

/**
 * DOM-Existenz-Pruefung fuer einen Anker. Wird von allen zustandsabhaengigen
 * Steps als `skipIf` genutzt — die Directive spiegelt den Wert als
 * gleichnamiges Attribut auf das Host-Element (siehe
 * `onboarding-anchor.directive.ts`), deshalb ist der Attribut-Selektor
 * derselbe String wie der Enum-Wert.
 */
function anchorMissing(anchor: AnchorId): boolean {
  return (
    typeof document !== 'undefined' &&
    !document.querySelector(`[onboardingAnchor="${anchor}"]`)
  );
}

/**
 * Ansicht „Aufträge" nicht verfügbar? Der Ansichts-Umschalter trägt
 * `data-projects-view` nur, solange „Aufträge nutzen" an ist
 * (`tasks-overview-page.component.ts`). Fehlt der Umschalter ganz
 * (Erstnutzung, Telefon ohne Umschalter), gilt das ebenso.
 */
function projectsViewMissing(): boolean {
  return (
    typeof document !== 'undefined' &&
    !document.querySelector(
      `[onboardingAnchor="${AnchorId.TasksViewSwitch}"][data-projects-view]`,
    )
  );
}

/**
 * Setup-Tour „Aufgaben" — SIEBTER und letzter Schritt des Setup-Funnels
 * (priority 70), direkt nach der Zeiterfassung (60).
 *
 * Ziel ist ein echtes Datum, kein Durchklicken: die Tour endet erst, wenn der
 * Server eine Aufgabe angelegt hat (`tour:task-created` feuert nur im
 * Erfolgsfall von `POST /api/Tasks`). Wer schon Aufgaben hat, bekommt die Tour
 * per Smart-Skip des {@link CompletionDetector} gar nicht erst angeboten.
 *
 * Voraussetzungen im Graphen (`tour-prerequisites.ts`): `first-department` UND
 * `add-skills`. Beides sind die Zuweisungsziele einer Aufgabe — ohne Abteilung
 * und ohne Qualifikation bleibt im Schritt „Zuweisen an" nur die Einzelperson,
 * und der fachliche Kern der Tour (Gruppenziele) laesst sich nicht zeigen.
 *
 * ⚠️ Zwei Steps sind zustandsabhaengig. Kennzahlen-Leiste und Steuerband
 * haengen in `tasks-overview-page.component.ts` beide an `*ngIf="!showFirstUse()"`
 * — und genau im Onboarding-Fall (frische Firma, null Aufgaben) ist
 * `showFirstUse()` TRUE. Beide Steps tragen deshalb `skipIf` + `optional`, und
 * die Tour setzt `renumberVisibleSteps`, damit der Zaehler nicht „2/7 → 4/7"
 * springt. Sobald Bestand da ist (Replay aus der Checkliste), laufen sie mit.
 *
 * Zwei weitere Steps (AP-62) gibt es nur mit „Aufträge nutzen" (E-05):
 * `tasks-projects-view` und `tasks-project-kind`. Bei einer frischen Firma ist
 * der Schalter aus (Standard), beide entfallen dann ebenfalls per `skipIf`.
 *
 * ⚠️ Feature-Gate: `TourConfig` kennt KEIN Feld fuer ein Abo-Feature. Das
 * Abo-Feature `TASK_MANAGEMENT` (Seed-Id 64) laesst sich hier also NICHT
 * deklarativ voraussetzen — abgedeckt ist nur die Rechte-Achse ueber
 * `requiredPermissions`. Siehe Bericht/Kommentar am `mainOrder`-Eintrag im
 * Checklist-FAB.
 */
export const TASKS_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.Tasks,
  version: 1,
  title: 'CHECKLIST.TASKS.TITLE',
  description: 'CHECKLIST.TASKS.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Rechte-Achse des Aufgaben-Gates (06-ENTRYPOINT-NAV.md §3.2, DEC-09):
  // mindestens EIN View-Recht. ODER-Gruppe, exakt die drei Rechte, die auch
  // `hasAnyTaskViewRight()` in `modules/tasks/nav/tasks-permission.helper.ts`
  // prueft.
  //
  // Fail-closed und heute wirksam „aus": `UserPermissionConfig` hat noch gar
  // keinen `Tasks`-Block, alle drei Pfade resolven auf `undefined` → die Tour
  // erscheint fuer niemanden. Das ist die gewollte Richtung (nie ein Recht
  // clientseitig defaulten); sie kippt automatisch an, sobald das Backend den
  // Block liefert.
  requiredPermissions: [
    [
      'Tasks.CanViewOwnTasks',
      'Tasks.CanViewTeamTasks',
      'Tasks.CanViewAllTasks',
    ],
  ],
  // Wie die Zeiterfassungs-Tour: kein FirstVisit/AfterSuccess-Nudge. Die Tour
  // wird aus der Setup-Checkliste bzw. dem Hilfe-Hub gestartet und via Resume
  // fortgesetzt. Ein Auto-Nudge waere hier riskant, weil `/tasks` ohne das
  // Abo-Feature auf die Abo-Seite umleitet (siehe `TasksFeatureGuard`).
  triggers: [TourTrigger.Manual, TourTrigger.Resume],
  triggerContext: {
    route: '/tasks',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  priority: 70,
  estimatedDurationSec: 300,
  category: 'setup',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'tasks-overview',
      title: 'CHECKLIST.TASKS.CHAPTER_OVERVIEW',
      steps: [
        {
          // Welcome-Step: „wo die Aufgaben leben". Anker ist die Seitenwurzel —
          // ein Anker am Navigationseintrag der Sidebar ist nicht moeglich,
          // weil `XdsNavItem` (shared/ui/nav-list) kein Anker-Feld hat und die
          // Zeilen datengetrieben gerendert werden. Statt die geteilte
          // Nav-Komponente umzubauen, navigiert die Tour selbst hin.
          id: 'tasks-welcome',
          anchor: AnchorId.TasksPage,
          spotlightAnchor: AnchorId.TasksPage,
          title: 'TUTORIAL.TASKS.V1.WELCOME.TITLE',
          content: 'TUTORIAL.TASKS.V1.WELCOME.CONTENT',
          placement: 'bottom',
          align: 'start',
          showNextButton: true,
          onBeforeShow: goToTasks,
        },
        {
          // Kennzahlen-Leiste. Nur mit Bestand im DOM (s. Kopfkommentar).
          id: 'tasks-stats',
          anchor: AnchorId.TasksStatStrip,
          spotlightAnchor: AnchorId.TasksStatStrip,
          title: 'TUTORIAL.TASKS.V1.STATS.TITLE',
          content: 'TUTORIAL.TASKS.V1.STATS.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          optional: true,
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TasksStatStrip),
        },
        {
          // Steuerband (Suche, Abteilung, Status, Faelligkeits-Chips).
          // Ebenfalls nur mit Bestand im DOM.
          id: 'tasks-filters',
          anchor: AnchorId.TasksFilterBar,
          spotlightAnchor: AnchorId.TasksFilterBar,
          title: 'TUTORIAL.TASKS.V1.FILTERS.TITLE',
          content: 'TUTORIAL.TASKS.V1.FILTERS.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          optional: true,
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TasksFilterBar),
        },
        {
          // Ansicht „Aufträge" (AP-62). Nur mit „Aufträge nutzen" — aus heißt
          // unsichtbar (E-05), dann gibt es das Segment nicht und der Schritt
          // entfällt. Das Segment selbst hat keinen eigenen Anker (die Segmente
          // werden datengetrieben gerendert); die Übersicht spiegelt den
          // Schalter deshalb als `data-projects-view` an den Umschalter.
          id: 'tasks-projects-view',
          anchor: AnchorId.TasksViewSwitch,
          spotlightAnchor: AnchorId.TasksViewSwitch,
          title: 'TUTORIAL.TASKS.V1.PROJECTS_VIEW.TITLE',
          content: 'TUTORIAL.TASKS.V1.PROJECTS_VIEW.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          optional: true,
          onBeforeShow: goToTasks,
          skipIf: () => projectsViewMissing(),
        },
        {
          // Aktiver Klick-Step. Spotlight liegt auf BEIDEN Anlege-Wegen: dem
          // Kopf-Button und — im Erstnutzungs-Zustand — den Kacheln des
          // Leerzustands. Der Nutzer sieht damit, dass beide Wege gelten
          // (dasselbe Muster wie `add-department` in der Abteilungs-Tour).
          //
          // Advance via CustomEvent aus `tasks-sheet.service.ts`: ein Dispatch
          // deckt alle Einstiege ab, auch „Mehr…" im Schnell-Anlegen.
          id: 'tasks-create',
          anchor: AnchorId.TasksCreateButton,
          spotlightAnchor: AnchorId.TasksCreateButton,
          additionalSpotlightAnchors: [AnchorId.TasksFirstUse],
          title: 'TUTORIAL.TASKS.V1.CREATE.TITLE',
          content: 'TUTORIAL.TASKS.V1.CREATE.CONTENT',
          placement: 'bottom',
          align: 'end',
          advanceOnDocumentEvent: 'tour:task-create-sheet-opened',
          showNextButton: false,
          onBeforeShow: goToTasks,
        },
      ],
    },
    {
      id: 'tasks-sheet',
      title: 'CHECKLIST.TASKS.CHAPTER_SHEET',
      steps: [
        {
          // Kern-Step: das Ziel. Hier zahlen sich die beiden Voraussetzungen
          // sichtbar aus — die Auswahlliste enthaelt Personen, Abteilungen UND
          // Qualifikationen, weil Schritt 2 und 3 des Funnels sie angelegt
          // haben. Der Text sagt das ausdruecklich.
          //
          // `popoverOutsideDialog`: der Dialog ist 940px breit, ein Popover
          // INNERHALB wuerde das Feld verdecken, das es erklaert.
          id: 'tasks-target',
          anchor: AnchorId.TaskSheetTarget,
          spotlightAnchor: AnchorId.TaskSheetTarget,
          title: 'TUTORIAL.TASKS.V1.TARGET.TITLE',
          content: 'TUTORIAL.TASKS.V1.TARGET.CONTENT',
          placement: 'right',
          showNextButton: true,
          // Erster Step im offenen Sheet — der Vorgaenger (Anlegen-Button)
          // liegt hinter dem Dialog, ein Zuruecksprung waere unsichtbar.
          showPrevButton: false,
          dialogContext: {
            attachToDialog: true,
            waitForOpen: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TaskSheetDialog),
          resumeRecoveryStepId: 'tasks-create',
        },
        {
          // Checkliste. Die Sektion ist `collapsible` und startet beim Anlegen
          // eingeklappt — der Host bleibt aber im DOM, der Spotlight rahmt
          // also die Sektionsueberschrift. Inhaltlich wichtig: ein Punkt kann
          // einen Nachweis verlangen (Haken, Zahl, Text, Foto).
          id: 'tasks-checklist',
          anchor: AnchorId.TaskSheetChecklist,
          spotlightAnchor: AnchorId.TaskSheetChecklist,
          title: 'TUTORIAL.TASKS.V1.CHECKLIST.TITLE',
          content: 'TUTORIAL.TASKS.V1.CHECKLIST.CONTENT',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          optional: true,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TaskSheetChecklist),
          resumeRecoveryStepId: 'tasks-create',
        },
        {
          // Umschalter „Einzelne Aufgabe | Auftrag mit Schritten" (AP-62). Steht
          // nur beim Anlegen und nur mit „Aufträge nutzen" im DOM (E-05), sonst
          // entfällt der Schritt. Bewusst NACH Ziel und Checkliste: Wer hier
          // umschaltet, verliert deren Anker (das Formular wechselt), der
          // Speichern-Knopf bleibt — und `tour:task-created` feuert seit AP-62
          // auch nach dem Anlegen eines Auftrags.
          id: 'tasks-project-kind',
          anchor: AnchorId.TaskSheetKind,
          spotlightAnchor: AnchorId.TaskSheetKind,
          title: 'TUTORIAL.TASKS.V1.PROJECT_KIND.TITLE',
          content: 'TUTORIAL.TASKS.V1.PROJECT_KIND.CONTENT',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          optional: true,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TaskSheetKind),
          resumeRecoveryStepId: 'tasks-create',
        },
        {
          // Abschluss. `tour:task-created` feuert in
          // `task-create-edit-sheet.component.ts:handleCreateResult` — also
          // erst nach einem erfolgreichen POST. Bei Feldfehlern bleibt die
          // Tour hier stehen, der Nutzer korrigiert und drueckt erneut.
          id: 'tasks-save',
          anchor: AnchorId.TaskSheetSave,
          spotlightAnchor: AnchorId.TaskSheetSave,
          title: 'TUTORIAL.TASKS.V1.SAVE.TITLE',
          content: 'TUTORIAL.TASKS.V1.SAVE.CONTENT',
          placement: 'top',
          align: 'end',
          advanceOnDocumentEvent: 'tour:task-created',
          showNextButton: false,
          showPrevButton: false,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTasks,
          skipIf: () => anchorMissing(AnchorId.TaskSheetSave),
          resumeRecoveryStepId: 'tasks-create',
        },
      ],
    },
  ],
};
